import { useMemo } from 'react'

import { HookedRecordResult } from '@ingestro/importer-react'
import { UniversalImporterValueObjectsImportTypeEnum } from 'generated/resources/datatable.graphql'
import {
  SchemaDataFieldsDocument,
  TargetsDataDocument,
  UniversalImporterImportProcessMetadataInput,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

import { type EditionData } from './types'

type UseEditionDataProps = {
  companyId: string
  importType: UniversalImporterValueObjectsImportTypeEnum
  metadata: UniversalImporterImportProcessMetadataInput[]
  isEdition: boolean
}

interface UseEditionDataResult {
  editionData: EditionData
  loading: boolean
  error?: Error
}

export const useEditionData = ({
  companyId,
  importType,
  metadata,
  isEdition,
}: UseEditionDataProps): UseEditionDataResult => {
  const {
    data: schemaDataFieldsQuery,
    loading: schemaDataFieldsLoading,
    error: schemaError,
  } = useQuery(SchemaDataFieldsDocument, {
    variables: {
      companyId,
      importType,
      metadata,
    },
    skip: !isEdition,
  })

  // Filter field names to only include visible fields (not hidden)
  const fieldNames = useMemo(() => {
    return (
      schemaDataFieldsQuery?.universalImporter.schemaAdapters
        .filter((item) => !item.hidden || isEdition) // Exclude hidden fields only when isEdition is false
        .map((item) => item.key) ?? []
    )
  }, [schemaDataFieldsQuery?.universalImporter.schemaAdapters])

  const {
    data: schemaDataQuery,
    loading: targetsLoading,
    error: targetsError,
  } = useQuery(TargetsDataDocument, {
    variables: {
      fieldNames,
      importType,
      metadata,
    },
    skip: schemaDataFieldsLoading || !schemaDataFieldsQuery || !isEdition,
  })

  // Check if value is a JSON array string and convert it
  const processValue = (value: string | null | undefined) => {
    if (typeof value === 'string' && value.startsWith('[') && value.endsWith(']')) {
      const parsed = JSON.parse(value)
      if (Array.isArray(parsed) && parsed.every((option) => typeof option === 'string')) {
        return parsed
      } else {
        return value
      }
    }
    return value
  }

  const editionData = useMemo(() => {
    if (!isEdition || !schemaDataQuery?.universalImporter.schemaData) {
      return []
    }

    return schemaDataQuery.universalImporter.schemaData.map((item) => {
      const record: Record<string, string | string[] | HookedRecordResult> = {}
      item.fields?.forEach(({ key, value }) => {
        if (key) {
          if (key === 'errors' && value) {
            record[key] = {
              value: value,
              info: [
                {
                  message: String(value),
                  level: 'info',
                },
              ],
            }
          } else {
            record[key] = processValue(value) ?? ''
          }
        }
      })
      return record
    })
  }, [schemaDataQuery?.universalImporter.schemaData, isEdition])

  return {
    editionData,
    loading: isEdition ? schemaDataFieldsLoading || targetsLoading : false,
    error: isEdition ? schemaError || targetsError : undefined,
  }
}
